import { useCallback, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'feizhou-web:theme';

function isValid(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

function readStoredTheme(): ThemeMode | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isValid(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** App 打开协议页会带 ?theme=dark|light，优先级最高 */
function readUrlTheme(): ThemeMode | null {
  try {
    const direct = new URLSearchParams(window.location.search).get('theme');
    const hashQuery = window.location.hash.includes('?')
      ? new URLSearchParams(
          window.location.hash.slice(window.location.hash.indexOf('?')),
        ).get('theme')
      : null;
    const value = direct ?? hashQuery;
    return isValid(value) ? value : null;
  } catch {
    return null;
  }
}

function systemTheme(): ThemeMode {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** URL 参数 > 上次选择 > 系统偏好 */
export function resolveTheme(): ThemeMode {
  return readUrlTheme() ?? readStoredTheme() ?? systemTheme();
}

export function applyTheme(mode: ThemeMode): void {
  document.documentElement.dataset.theme = mode;
  document.documentElement.style.colorScheme = mode;
  document.documentElement.style.background =
    mode === 'dark' ? '#0f172a' : '#f5f6fa';
}

function persistTheme(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* 隐私模式写不了，忽略 */
  }
}

export function useThemeMode(): { mode: ThemeMode; toggle: () => void } {
  const [mode, setMode] = useState<ThemeMode>(() => resolveTheme());

  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  // 用户没手动选过时，跟随系统切换
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (!readStoredTheme() && !readUrlTheme()) {
        setMode(query.matches ? 'dark' : 'light');
      }
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    setMode((prev) => {
      const next: ThemeMode = prev === 'dark' ? 'light' : 'dark';
      persistTheme(next);
      return next;
    });
  }, []);

  return { mode, toggle };
}
